import { useEffect } from 'react'
import type { NextPage } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { getGenericStaticPaths } from '@/utils/nextUtils/staticPages'
import { writeToSessionStorage } from '@/utils/session-storage'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { SubmitView } from '@/views/SubmitView'

interface SubmitPageProps {
  locale: string
}

const SubmitPage: NextPage<SubmitPageProps> = ({ locale }) => {
  const { t } = useTranslate('create')
  const url = `${getBasePath(locale)}${paths.submit.index}`
  const { query } = useSafeRouter()
  const title = t('angelCreateMetaTitleV2', 'Submit Your Film or Show | Angel')
  const description = t(
    'angelCreateMetaDescriptionV2',
    'Submit your film or show to the Angel Guild. Get audience votes, feedback, and a chance at distribution by Angel.',
  )

  useEffect(() => {
    if (query.promoCode) {
      const promoCode = getItemOrFirstEntry(query.promoCode)
      if (promoCode) {
        writeToSessionStorage('torchSubmissionPromoCode', promoCode)
      }
    }
  }, [query.promoCode])

  return (
    <>
      <Seo
        canonical={url}
        description={description}
        openGraph={{ title, description, url }}
        path={paths.submit.index}
        title={title}
      />
      <SubmitView />
    </>
  )
}

export const getStaticPaths = getGenericStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<SubmitPageProps>(
  { filePath: 'pages/[locale]/submit/index.tsx' },
  async ({ locale }) => {
    const translations = await loadTranslations(locale, ['common', 'create', 'filmmaker'])

    return {
      props: {
        locale,
        ...translations,
      },
      revalidate: Durations.ONE_DAY_IN_SECONDS,
    }
  },
)

export default SubmitPage
